import { type FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericVariantsView from "./GenericVariantsView.js";
import { renderGenomeAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import type { InputTableQuerySpec } from "@/graphql.ts";
import { type IGenomeState } from "@/redux/modules/genome.ts";
import { setGroupMutantGenomeIds } from "@/redux/modules/saved-group.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.js";

const GenomeVariantsView: FC = () => {
  const { genome, isLoading } = useSelector<IAppState, IGenomeState>((state) => state.genome);
  const refGenomeId = useSelector<IAppState>((state) => state.auth.session.params?.referenceGenome);
  const mutantGenomeIds = useSelector<IAppState, number[]>((state) => state.savedGroup.visibleMutantGenomeIds);
  const viewId = useURLViewID();
  const { genomeId } = useParams<"genomeId">();
  const dispatch = useDispatch<ReduxDispatch>();

  const analysisGroup = !isLoading ? genome?.variantAnalysisGroups?.[0] : undefined; // for derived variants

  // cleanup of the selection of mutant genomes ids, since it might be incompatible with the next genome's reference library
  useEffect(() => {
    return () => {
      dispatch(setGroupMutantGenomeIds([]));
    };
  }, []);

  const getTableQuerySpec = (): InputTableQuerySpec | undefined => {
    if (isLoading || !genome) {
      return undefined;
    }

    if (genome.hasVcfGenomeAnalysis && analysisGroup) {
      return {
        id: Number(genomeId),
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: ENTITY_TYPE.ORGANISM,
        resultType: ENTITY_TYPE.VCF_VARIANT,
        restrictions: {
          variantGenomeIds: mutantGenomeIds,
        },
      } as InputTableQuerySpec;
    } else {
      return refGenomeId
        ? ({
            id: Number(genomeId),
            idType: DATATABLE_ID_TYPE.ENTITY,
            queryType: ENTITY_TYPE.ORGANISM,
            resultType: ENTITY_TYPE.VARIANT,
            restrictions: {
              refGenomeId: refGenomeId,
            },
          } as InputTableQuerySpec)
        : undefined;
    }
  };

  const tableSpec = getTableQuerySpec();

  return (
    <GenericVariantsView
      title={genome?.name ?? undefined}
      // Empty string are falsy
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      subtitle={genome?.description || (genome?.hasVcfGenomeAnalysis ? "List of Variants" : "List of Derived Variants")}
      headerAttributes={[
        <AttributesListWidget attributes={renderGenomeAttributesWidgets(genome)} key={"genome-genome-view-widget-1"} />,
      ]}
      tableSpec={tableSpec}
      variantAnalysisGroup={analysisGroup ?? undefined}
      viewId={viewId ?? undefined}
      getSummaryEntityName={() => genome?.name ?? ""}
      hideAnalyzeBtn={true}
      hideVariantGenomesBtn={true}
    />
  );
};

export default GenomeVariantsView;
